import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import FAQAccordion from "@/components/FAQAccordion";
import JsonLd from "@/components/JsonLd";
import useSEO from "@/lib/useSEO";
import {
  faqPageSchema,
  localBusinessSchema,
  breadcrumbSchema,
  SITE_URL,
} from "@/lib/schema";
import {
  CITIES,
  STENO_FAQ,
  LEGAL_FAQ,
  STENO_KEYWORDS,
  LEGAL_KEYWORDS,
} from "@/data/content";

const PRODUCT_CONFIG = {
  steno: {
    name: "StenoPaceMaster",
    color: "#1E3A8A",
    colorHover: "#1D4ED8",
    accentBg: "#EFF6FF",
    label: "Stenography Training",
    site: "https://stenopacemaster.com",
    siteLabel: "stenopacemaster.com",
    statKey: "stenoStats",
    statLabel: (s) => `${s.reporters} working reporters · avg ${s.avgSpeed}`,
    faq: STENO_FAQ,
    keywords: STENO_KEYWORDS,
    pitch: (city) =>
      `If you're a court reporter, captioner or steno student working in ${city}, StenoPaceMaster gives you the structured online practice program top professionals use to push speed, prep for certifications, and stay sharp.`,
    benefits: [
      {
        t: "Built around RPR / state CSR formats",
        b: "Timed dictation at Literary 180, Jury Charge 200, Q&A 225 (and faster) graded to NCRA accuracy standards.",
      },
      {
        t: "Daily structure, not random takes",
        b: "A weekly plan calibrated to your current speed — finger drills, accuracy work, push speed, dictionary maintenance.",
      },
      {
        t: "Realtime & broadcast captioning drills",
        b: "Fast-news, sports rosters, untranslate tracking — purpose-built for captioning professionals.",
      },
    ],
    sub: "Online stenography practice for court reporters, CART providers and broadcast captioners in",
  },
  draftmaster: {
    name: "DraftMaster.ai",
    color: "#065F46",
    colorHover: "#047857",
    accentBg: "#ECFDF5",
    label: "AI Legal Drafting",
    site: "https://draftmaster.ai",
    siteLabel: "draftmaster.ai",
    statKey: "legalStats",
    statLabel: (s) => `${s.firms} firms · ${s.barAdmits} bar admissions`,
    faq: LEGAL_FAQ,
    keywords: LEGAL_KEYWORDS,
    pitch: (city) =>
      `Attorneys, paralegals and corporate legal teams across ${city} use DraftMaster.ai to draft contracts, motions, briefs and correspondence — with their own clause library and an accuracy audit on every output.`,
    benefits: [
      {
        t: "Drafts that follow your house style",
        b: "Upload your clause library; outputs prefer your wording over generic AI defaults.",
      },
      {
        t: "Citation safety on litigation work",
        b: "Every cite is sourced from your loaded materials or flagged for verification before export.",
      },
      {
        t: "Built for the legal stack",
        b: "From NDAs to real-estate purchase agreements, motions to employment offer letters.",
      },
    ],
    sub: "AI-powered legal drafting and contract automation for attorneys in",
  },
};

export default function CityPage({ product }) {
  const { citySlug } = useParams();
  const city = CITIES.find((c) => c.slug === citySlug);
  const config = PRODUCT_CONFIG[product];

  const validRoute = Boolean(city && config);
  const safeCity = city || CITIES[0];
  const pageUrl =
    SITE_URL +
    (product === "steno"
      ? `/steno/${safeCity.slug}`
      : `/legal-ai/${safeCity.slug}`);
  const productLabel = product === "steno" ? "Stenography" : "Legal AI";

  const seoTitle =
    product === "steno"
      ? `Stenography Training in ${safeCity.short} — Speed Building & RPR Practice | StenoPaceMaster`
      : `AI Legal Drafting in ${safeCity.short} — Automated Contract Drafting Tool | DraftMaster.ai`;
  const seoDesc =
    product === "steno"
      ? `Online stenography speed-building, RPR/RMR/CRR certification practice and realtime drills for court reporters, CART providers and broadcast captioners in ${safeCity.name}. Powered by StenoPaceMaster.`
      : `AI-powered legal drafting, contract automation and litigation document creation for attorneys, paralegals and corporate legal teams in ${safeCity.name}. Powered by DraftMaster.ai.`;
  const seoKeywords =
    product === "steno"
      ? `stenography training ${safeCity.short}, court reporting ${safeCity.short}, steno speed building ${safeCity.short}, RPR exam practice ${safeCity.short}, online stenography course ${safeCity.short}`
      : `AI legal drafting ${safeCity.short}, automated contract drafting tool ${safeCity.short}, AI for law firms ${safeCity.short}, legal document automation ${safeCity.short}, AI tool for lawyers ${safeCity.short}`;

  useSEO({
    title: seoTitle,
    description: seoDesc,
    keywords: seoKeywords,
    url: pageUrl,
    image: safeCity.image,
    noindex: !validRoute,
  });

  if (!validRoute) return <Navigate to="/" replace />;

  const otherPath =
    product === "steno"
      ? `/legal-ai/${city.slug}`
      : `/steno/${city.slug}`;
  const otherLabel = product === "steno" ? "Legal AI" : "Stenography";

  return (
    <div data-testid={`city-page-${product}-${city.slug}`}>
      <JsonLd
        id={`city-${product}-${city.slug}-business`}
        data={localBusinessSchema({
          city: city.short,
          state: city.state,
          url: pageUrl,
          product,
          productUrl: config.site,
        })}
      />
      <JsonLd
        id={`city-${product}-${city.slug}-faq`}
        data={faqPageSchema(config.faq)}
      />
      <JsonLd
        id={`city-${product}-${city.slug}-breadcrumbs`}
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          {
            name: productLabel,
            url:
              SITE_URL +
              (product === "steno" ? "/stenopacemaster" : "/draftmaster"),
          },
          { name: city.short, url: pageUrl },
        ])}
      />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src={city.image}
            alt={city.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-28 lg:py-40 text-white">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-white/70 mb-6"
          >
            <MapPin size={14} />
            <span>{config.label}</span>
            <span className="text-white/30">/</span>
            <span>{city.name}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="city-h1"
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl"
          >
            {config.sub} {city.short}.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl"
          >
            {city.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <a
              href={config.site}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="city-cta-outbound"
              className="btn-sharp bg-white text-black hover:bg-white/90 inline-flex items-center gap-2"
            >
              Visit {config.siteLabel} <ArrowUpRight size={14} />
            </a>
            <Link
              to={otherPath}
              data-testid="city-cta-cross"
              className="btn-sharp border border-white text-white hover:bg-white hover:text-black"
            >
              {otherLabel} in {city.short}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* PITCH */}
      <section
        className="py-24 lg:py-32 border-b border-black/10"
        style={{ background: config.accentBg }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p
              className="text-xs tracking-[0.3em] uppercase font-semibold mb-4"
              style={{ color: config.color }}
            >
              Why {config.name} in {city.short}
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              Local context. Real outcomes.
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed max-w-sm">
              {config.pitch(city.short)}
            </p>
            <p className="mt-6 text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">
              {config.statLabel(city[config.statKey])}
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 gap-6">
            {config.benefits.map((b, i) => (
              <div
                key={i}
                data-testid={`city-benefit-${i}`}
                className="bg-white border border-black/10 p-8"
              >
                <p
                  className="text-xs tracking-[0.2em] uppercase font-semibold mb-3"
                  style={{ color: config.color }}
                >
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="font-serif-display text-2xl mb-2">{b.t}</p>
                <p className="text-sm text-gray-700 leading-relaxed">{b.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Frequently asked
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              {config.label} questions, {city.short} edition.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={config.faq} testIdPrefix="city-faq" />
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section
        className="py-24 lg:py-32 text-white"
        style={{ background: config.color }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-4">
              Start today
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none">
              Get the {config.label.toLowerCase()} program built for {city.short}.
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={config.site}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="city-bottom-cta"
                className="btn-sharp bg-white hover:bg-white/90 inline-flex items-center gap-2"
                style={{ color: config.color }}
              >
                Open {config.siteLabel} <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          <div>
            <LeadForm
              interest={product}
              source={`city-${product}-${city.slug}`}
              city={city.short}
              variant="dark"
              outboundUrl={config.site}
              testIdPrefix="city-lead"
              ctaLabel="Get the City Guide"
            />
          </div>
        </div>
      </section>

      {/* KEYWORDS */}
      <section className="py-16 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-6">
            Related searches · {city.short}
          </p>
          <div className="flex flex-wrap gap-2">
            {config.keywords.slice(0, 20).map((k) => (
              <span
                key={k}
                className="text-xs px-3 py-1.5 border border-black/15 text-gray-700"
              >
                {k} in {city.short}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
